import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    input,
    output,
    signal,
    viewChild,
    ViewEncapsulation,
} from '@angular/core';
import { IQuizEditItem, IQuizQuestionAnswer } from '@fi-app/fi-onboarding/fi-quiz/fi-quiz';
import { isDefined } from '@fi-app/shared/utils';

const STEP_WIDTH = 18;
const ACTIVE_STEP_WIDTH = 32;
const STEP_GAP = 32;
const STEP_SIZE = STEP_WIDTH + STEP_GAP;
const ACTIVE_STEP_EXTRA_WIDTH = ACTIVE_STEP_WIDTH - STEP_WIDTH;
const BUTTON_OVERLAY_WIDTH = 24;
const SCROLL_EDGE_THRESHOLD = 0.01;

@Component({
    selector: 'fi-quiz-progress-stepper',
    templateUrl: './quiz-progress-stepper.component.html',
    styleUrl: './quiz-progress-stepper.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'fi-quiz-progress-stepper',
        '[class.result-mode]': 'isResultMode()',
        '[class.has-scroll-overflow]': 'hasOverflow()',
    },
    standalone: false,
})
export class FiQuizProgressStepperComponent {
    public quiz = input.required<IQuizEditItem>();
    public currentQuestionIndex = input.required<number>();
    public answeredQuestions = input.required<IQuizQuestionAnswer[]>();
    public correctAnsweredQuestions = input<IQuizQuestionAnswer[]>();
    public isReportModeQuizNotCompletedOrInProgress = input<boolean>(false);
    public questionIndexSelected = output<number>();
    public isResultMode = input<boolean>(false);

    private readonly destroyRef = inject(DestroyRef);
    private readonly questionsContainerEl = viewChild.required<ElementRef<HTMLElement>>('questionsContainer');
    private readonly questionCount = computed(() => this.quiz().Questions.length);

    protected readonly scrollOffset = signal(0);
    private readonly questionsContainerWidth = signal(0);

    private readonly currentQuestionIndexEffect = effect(() => {
        const currentQuestionIndex = this.currentQuestionIndex();
        const viewportWidth = this.questionsContainerWidth();
        const maxScrollOffset = this.maxScrollOffset();

        this.scrollOffset.update((offset) => {
            if (viewportWidth === 0 || !this.hasOverflow()) {
                return 0;
            }

            const { left: visibleViewportLeft, right: visibleViewportRight } = this.getVisibleViewportBounds(
                offset,
                viewportWidth,
                maxScrollOffset,
            );
            const stepLeft = this.getStepLeft(currentQuestionIndex, currentQuestionIndex);
            const stepRight = this.getStepRight(currentQuestionIndex, currentQuestionIndex);
            const minVisibleOffset = Math.max(0, Math.ceil((stepRight - visibleViewportRight) / STEP_SIZE));
            const maxVisibleOffset = Math.max(0, Math.floor((stepLeft - visibleViewportLeft) / STEP_SIZE));

            if (offset < minVisibleOffset) {
                return Math.min(minVisibleOffset, maxScrollOffset);
            }
            if (offset > maxVisibleOffset) {
                return Math.min(maxVisibleOffset, maxScrollOffset);
            }

            return offset;
        });
    });

    protected readonly hasOverflow = computed(() => this.getTrackWidth() > this.questionsContainerWidth());

    private readonly maxScrollOffset = computed(() => {
        const viewportWidth = this.questionsContainerWidth();
        const questionCount = this.questionCount();

        if (viewportWidth === 0 || questionCount === 0 || !this.hasOverflow()) {
            return 0;
        }

        const hiddenWidth = this.getTrackWidth() - viewportWidth;
        return Math.max(0, hiddenWidth / STEP_SIZE);
    });

    protected readonly translateX = computed(() => (this.hasOverflow() ? -this.scrollOffset() * STEP_SIZE : 0));

    protected readonly showLeftButton = computed(
        () => this.hasOverflow() && this.scrollOffset() > SCROLL_EDGE_THRESHOLD,
    );
    protected readonly showRightButton = computed(
        () => this.hasOverflow() && this.maxScrollOffset() - this.scrollOffset() > SCROLL_EDGE_THRESHOLD,
    );
    protected readonly canDragScroll = computed(() => this.showLeftButton() || this.showRightButton());

    private dragStartScrollOffset = 0;

    constructor() {
        effect(() => {
            const max = this.maxScrollOffset();
            this.scrollOffset.update((v) => Math.min(Math.max(0, v), max));
        });

        afterNextRender(() => {
            const resizeObserver = new ResizeObserver(() => {
                this.questionsContainerWidth.set(this.questionsContainerEl().nativeElement.offsetWidth);
            });

            resizeObserver.observe(this.questionsContainerEl().nativeElement);
            this.destroyRef.onDestroy(() => resizeObserver.disconnect());
        });
    }

    protected isCurrentQuestion(questionIndex: number): boolean {
        return this.currentQuestionIndex() === questionIndex;
    }

    protected isAnsweredQuestion(questionId: number): boolean {
        return this.answeredQuestions().some(
            (answer) => answer.QuizQuestionId === questionId && !!answer.Value?.length,
        );
    }

    protected isCorrectAnsweredQuestion(questionId: number): boolean | undefined {
        if (
            this.isReportModeQuizNotCompletedOrInProgress() ||
            !this.isAnsweredQuestion(questionId) ||
            !isDefined(this.correctAnsweredQuestions())
        ) {
            return undefined;
        }
        return this.correctAnsweredQuestions().some((answer) => answer.QuizQuestionId === questionId);
    }

    protected scrollLeft(): void {
        this.scrollOffset.update((v) => Math.max(0, v - 1));
    }

    protected scrollRight(): void {
        const max = this.maxScrollOffset();
        this.scrollOffset.update((v) => Math.min(max, v + 1));
    }

    protected onDragScrollStart(): void {
        this.dragStartScrollOffset = this.scrollOffset();
    }

    protected onDragScroll(dragDistance: number): void {
        const nextOffset = this.dragStartScrollOffset - dragDistance / STEP_SIZE;

        this.scrollOffset.set(this.clampScrollOffset(nextOffset));
    }

    private getVisibleViewportBounds(
        offset: number,
        viewportWidth: number,
        maxScrollOffset: number,
    ): { left: number; right: number } {
        return {
            left: offset > 0 ? BUTTON_OVERLAY_WIDTH : 0,
            right: viewportWidth - (offset < maxScrollOffset ? BUTTON_OVERLAY_WIDTH : 0),
        };
    }

    private getStepLeft(stepIndex: number, currentQuestionIndex: number): number {
        const offsetFromCurrentStep = stepIndex > currentQuestionIndex ? ACTIVE_STEP_EXTRA_WIDTH : 0;

        return stepIndex * STEP_SIZE + offsetFromCurrentStep;
    }

    private getStepRight(stepIndex: number, currentQuestionIndex: number): number {
        const stepWidth = stepIndex === currentQuestionIndex ? ACTIVE_STEP_WIDTH : STEP_WIDTH;

        return this.getStepLeft(stepIndex, currentQuestionIndex) + stepWidth;
    }

    private getTrackWidth(): number {
        const questionCount = this.questionCount();

        if (questionCount === 0) {
            return 0;
        }

        return (questionCount - 1) * STEP_SIZE + ACTIVE_STEP_WIDTH;
    }

    private clampScrollOffset(offset: number): number {
        return Math.min(this.maxScrollOffset(), Math.max(0, offset));
    }
}
